import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { fetchMe } from '../../../lib/meClient';
import { SettingsCard } from '../components/SettingsCard';
import {
  SETTINGS_BTN_DANGER_OUTLINE,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
  SETTINGS_TEXTAREA_CLASS,
} from '../utils/settingsSectionStyles';
import { AiShareFields, type AiShareMember, type AiVisibility } from '../../assistant/AiShareFields';
import { chatPath } from '../../../utils/dashboardRoutes';

type Skill = {
  id: string;
  name: string;
  description: string;
  visibility: AiVisibility;
  enabled: boolean;
  created_by: string;
  can_edit: boolean;
  can_toggle: boolean;
};

type SkillDetail = Skill & { instructions: string; grantee_user_ids?: string[] };

type Job = {
  id: string;
  name: string;
  description: string;
  prompt: string;
  cadence: 'daily' | 'weekly';
  time_local: string;
  weekday: number | null;
  enabled: boolean;
  next_run_at: string;
  last_status: string | null;
};

const WEEKDAYS = [
  { value: '1', label: 'Montag' },
  { value: '2', label: 'Dienstag' },
  { value: '3', label: 'Mittwoch' },
  { value: '4', label: 'Donnerstag' },
  { value: '5', label: 'Freitag' },
  { value: '6', label: 'Samstag' },
  { value: '7', label: 'Sonntag' },
];

function jobScheduleLabel(job: Job): string {
  if (job.cadence === 'daily') return `Täglich um ${job.time_local}`;
  const day = WEEKDAYS.find((item) => item.value === String(job.weekday ?? 1))?.label ?? 'Montag';
  return `Jeden ${day} um ${job.time_local}`;
}

const VISIBILITY_LABEL: Record<AiVisibility, string> = {
  private: 'Nur ich',
  members: 'Bestimmte Personen',
  instance: 'Ganze Instanz',
};

async function readError(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => ({})) as { error?: string };
  return body.error || fallback;
}

export function AssistantSettingsSection({ part = "all", embedded = false }: { part?: "all" | "skills" | "jobs"; embedded?: boolean }) {
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [userId, setUserId] = React.useState<string | null>(null);
  const [members, setMembers] = React.useState<AiShareMember[]>([]);
  const [skills, setSkills] = React.useState<Skill[]>([]);
  const [jobs, setJobs] = React.useState<Job[]>([]);
  const [query, setQuery] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [editingId, setEditingId] = React.useState<string | 'new' | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const me = await fetchMe();
      const role = me?.role;
      const admin = role === 'owner' || role === 'admin';
      setIsAdmin(admin);
      setUserId(me?.user?.id ?? null);
      const [skillRes, memberRes, jobRes] = await Promise.all([
        fetch('/api/ai/skills', { credentials: 'include' }),
        fetch('/api/me/members', { credentials: 'include' }),
        admin ? fetch('/api/ai/scheduled-jobs', { credentials: 'include' }) : Promise.resolve(null),
      ]);
      if (!skillRes.ok) throw new Error(await readError(skillRes, 'Skills konnten nicht geladen werden.'));
      const skillBody = await skillRes.json() as { skills?: Skill[] };
      setSkills(skillBody.skills ?? []);
      if (memberRes.ok) {
        const memberBody = await memberRes.json() as { members?: Array<{ user_id: string; display_name: string | null; email: string; invited?: boolean }> };
        setMembers((memberBody.members ?? [])
          .filter((member) => !member.invited && member.user_id !== me?.user?.id)
          .map((member) => ({ value: member.user_id, label: member.display_name?.trim() || member.email })));
      }
      if (jobRes) {
        if (!jobRes.ok) throw new Error(await readError(jobRes, 'Jobs konnten nicht geladen werden.'));
        const jobBody = await jobRes.json() as { jobs?: Job[] };
        setJobs(jobBody.jobs ?? []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Assistent konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const needle = query.trim().toLocaleLowerCase('de-DE');
  const visibleSkills = skills.filter((skill) => {
    if (!needle) return true;
    return skill.name.toLocaleLowerCase('de-DE').includes(needle) || skill.description.toLocaleLowerCase('de-DE').includes(needle);
  });
  const own = visibleSkills.filter((skill) => skill.created_by === userId);
  const shared = visibleSkills.filter((skill) => skill.created_by !== userId);

  const skillsBody = (
    <>
      {embedded ? (
        <div className="mb-4 flex items-center gap-2">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Skills suchen"
            aria-label="Skills suchen"
            className={`${SETTINGS_INPUT_CLASS} min-w-0 flex-1`}
          />
          {!editingId ? (
            <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={() => setEditingId('new')}>Neu</button>
          ) : null}
        </div>
      ) : (
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Skills suchen"
          aria-label="Skills suchen"
          className={`${SETTINGS_INPUT_CLASS} mb-4 w-full`}
        />
      )}
      {loading ? <InlineLoadingState label="Skills werden geladen …" /> : null}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {!loading && editingId ? (
        <SkillEditor
          skillId={editingId === 'new' ? null : editingId}
          members={members}
          isAdmin={isAdmin}
          onCancel={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); void load(); }}
        />
      ) : null}
      {!loading && !editingId ? (
        <div className="space-y-4">
          <SkillGroup title="Eigene" skills={own} onOpen={setEditingId} />
          <SkillGroup title="Geteilt mit mir" skills={shared} onOpen={setEditingId} />
        </div>
      ) : null}
    </>
  );

  return (
    <div className="space-y-5">
      {part !== "jobs" ? (
        embedded ? skillsBody : (
          <SettingsCard
            title="Skills"
            description="Arbeitsanweisungen für den Assistenten. Eingeschaltete Skills gelten für alle, die sie lesen dürfen."
            actions={editingId ? undefined : <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={() => setEditingId('new')}>Neu</button>}
          >
            {skillsBody}
          </SettingsCard>
        )
      ) : null}
      {isAdmin && part !== "skills" ? <JobsCard jobs={jobs} onChanged={() => void load()} embedded={embedded && part === "jobs"} /> : null}
      {!loading && !isAdmin && part === "jobs" ? (
        embedded ? (
          <div>
            <h2 className="text-lg font-bold text-app-text">Geplant</h2>
            <p className="mt-3 text-sm text-app-text-muted">Nur Inhaber und Admins können Aufgaben planen.</p>
          </div>
        ) : (
          <p className="text-sm text-app-text-muted">Nur Inhaber und Admins können Aufgaben planen.</p>
        )
      ) : null}
    </div>
  );
}

function SkillGroup({ title, skills, onOpen }: { title: string; skills: Skill[]; onOpen: (id: string) => void }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-app-text">{title}</h3>
      {skills.length === 0 ? <p className="text-sm text-app-text-muted">Keine Skills.</p> : (
        <ul className="divide-y divide-app-border rounded-md border border-app-border">
          {skills.map((skill) => (
            <li key={skill.id}>
              <button type="button" className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-app-hover" onClick={() => onOpen(skill.id)}>
                <span className="min-w-0">
                  <span className="block truncate text-sm text-app-text">{skill.name}</span>
                  <span className="block truncate text-xs text-app-text-muted">{skill.description}</span>
                </span>
                <span className="shrink-0 text-xs text-app-text-subtle">
                  {VISIBILITY_LABEL[skill.visibility]}{skill.enabled ? ' · Aktiv' : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SkillEditor({
  skillId,
  members,
  isAdmin,
  onCancel,
  onSaved,
}: {
  skillId: string | null;
  members: AiShareMember[];
  isAdmin: boolean;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [instructions, setInstructions] = React.useState('');
  const [visibility, setVisibility] = React.useState<AiVisibility>('private');
  const [granteeUserIds, setGranteeUserIds] = React.useState<string[]>([]);
  const [enabled, setEnabled] = React.useState(false);
  const [canEdit, setCanEdit] = React.useState(true);
  const [canToggle, setCanToggle] = React.useState(isAdmin);
  const [loading, setLoading] = React.useState(Boolean(skillId));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!skillId) return;
    let cancelled = false;
    fetch(`/api/ai/skills/${encodeURIComponent(skillId)}`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(await readError(res, 'Skill konnte nicht geladen werden.'));
        return res.json() as Promise<{ skill: SkillDetail }>;
      })
      .then((body) => {
        if (cancelled) return;
        const skill = body.skill;
        setName(skill.name);
        setDescription(skill.description);
        setInstructions(skill.instructions);
        setVisibility(skill.visibility);
        setGranteeUserIds(skill.grantee_user_ids ?? []);
        setEnabled(skill.enabled);
        setCanEdit(skill.can_edit);
        setCanToggle(skill.can_toggle);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Skill konnte nicht geladen werden.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [skillId]);

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = canEdit
      ? { name, description, instructions, visibility, granteeUserIds: visibility === 'members' ? granteeUserIds : [], ...(canToggle ? { enabled } : {}) }
      : { enabled };
    try {
      const res = await fetch(skillId ? `/api/ai/skills/${encodeURIComponent(skillId)}` : '/api/ai/skills', {
        method: skillId ? 'PATCH' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await readError(res, 'Skill konnte nicht gespeichert werden.'));
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Skill konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!skillId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/ai/skills/${encodeURIComponent(skillId)}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(await readError(res, 'Skill konnte nicht gelöscht werden.'));
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Skill konnte nicht gelöscht werden.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <InlineLoadingState label="Skill wird geladen …" />;

  return (
    <div className="space-y-3">
      <label className="block">
        <span className={SETTINGS_FIELD_LABEL}>Name</span>
        <input className={`${SETTINGS_INPUT_CLASS} mt-1.5 w-full`} value={name} disabled={!canEdit} onChange={(event) => setName(event.target.value)} />
      </label>
      <label className="block">
        <span className={SETTINGS_FIELD_LABEL}>Beschreibung</span>
        <input className={`${SETTINGS_INPUT_CLASS} mt-1.5 w-full`} value={description} disabled={!canEdit} onChange={(event) => setDescription(event.target.value)} />
      </label>
      <label className="block">
        <span className={SETTINGS_FIELD_LABEL}>Anweisung</span>
        <textarea className={`${SETTINGS_TEXTAREA_CLASS} mt-1.5 w-full`} rows={8} value={instructions} disabled={!canEdit} onChange={(event) => setInstructions(event.target.value)} />
      </label>
      {canEdit ? (
        <AiShareFields
          idPrefix="skill-editor"
          visibility={visibility}
          onVisibilityChange={setVisibility}
          granteeUserIds={granteeUserIds}
          onGranteesChange={setGranteeUserIds}
          members={members}
        />
      ) : <p className="text-sm text-app-text-muted">{VISIBILITY_LABEL[visibility]}</p>}
      {canToggle ? (
        <label className="flex items-center gap-2 text-sm text-app-text">
          <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
          Für den Assistenten aktivieren
        </label>
      ) : null}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      <div className="flex justify-end gap-2">
        {skillId && canEdit ? <button type="button" className={`${SETTINGS_BTN_DANGER_OUTLINE} mr-auto`} onClick={() => void remove()}>Löschen</button> : null}
        <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={onCancel}>Abbrechen</button>
        {canEdit || canToggle ? (
          <button type="button" className={SETTINGS_BTN_PRIMARY} disabled={saving} onClick={() => void save()}>Speichern</button>
        ) : null}
      </div>
    </div>
  );
}

function JobsCard({ jobs, onChanged, embedded = false }: { jobs: Job[]; onChanged: () => void; embedded?: boolean }) {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [editing, setEditing] = React.useState<Job | 'new' | null>(null);
  const [runningId, setRunningId] = React.useState<string | null>(null);
  const [testResult, setTestResult] = React.useState<{ chatId: string | null; error: string | null } | null>(null);

  const runNow = async (jobId: string) => {
    setRunningId(jobId);
    setTestResult(null);
    try {
      const res = await fetch(`/api/ai/scheduled-jobs/${encodeURIComponent(jobId)}/run`, {
        method: 'POST',
        credentials: 'include',
      });
      const body = await res.json().catch(() => ({})) as { chat_id?: string | null; error?: string };
      if (!res.ok) throw new Error(body.error || 'Testlauf ist fehlgeschlagen.');
      setTestResult({ chatId: body.chat_id ?? null, error: null });
      onChanged();
    } catch (err) {
      setTestResult({ chatId: null, error: err instanceof Error ? err.message : 'Testlauf ist fehlgeschlagen.' });
    } finally {
      setRunningId(null);
    }
  };

  const list = jobs.length === 0 ? (
    <p className="py-8 text-sm text-app-text-muted">Noch nichts geplant.</p>
  ) : (
    <ul className="space-y-2">
      {jobs.map((job) => (
        <li key={job.id} className="rounded-2xl border border-app-border bg-app-surface px-4 py-3.5">
          <div className="flex items-start justify-between gap-4">
            <button type="button" className="min-w-0 text-left" onClick={() => setEditing(job)}>
              <span className="block truncate text-sm font-medium text-app-text">{job.name}</span>
              <span className="mt-0.5 block text-sm text-app-text-muted">{jobScheduleLabel(job)}</span>
            </button>
            <button
              type="button"
              className="shrink-0 text-sm text-app-text-muted hover:text-app-text disabled:opacity-50"
              disabled={runningId === job.id || !job.enabled}
              onClick={() => void runNow(job.id)}
            >
              {runningId === job.id ? <LoadingSpinner size="sm" /> : 'Jetzt testen'}
            </button>
          </div>
          {job.prompt ? <p className="mt-2 line-clamp-2 text-sm text-app-text-subtle">{job.prompt}</p> : null}
          {!job.enabled ? <p className="mt-2 text-xs text-app-text-subtle">Pausiert</p> : null}
          {job.enabled && job.last_status === 'error' ? <p className="mt-2 text-xs text-red-600">Letzter Lauf fehlgeschlagen</p> : null}
        </li>
      ))}
    </ul>
  );
  const result = testResult ? (
    <p className={`mt-3 text-sm ${testResult.error ? 'text-red-600' : 'text-app-text-muted'}`}>
      {testResult.error ? testResult.error : 'Testlauf fertig.'}
      {!testResult.error && testResult.chatId && instanceId ? (
        <>
          {' '}
          <Link className="text-pipedrive-blue hover:underline" to={chatPath(instanceId, testResult.chatId)}>Antwort öffnen</Link>
        </>
      ) : null}
    </p>
  ) : null;
  const body = (
    <>
      {embedded ? (
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-app-text">Geplant</h2>
            <p className="mt-1 text-sm text-app-text-muted">Der Assistent schreibt dir zur gewählten Zeit.</p>
          </div>
          {!editing ? (
            <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={() => setEditing('new')}>Neu</button>
          ) : null}
        </div>
      ) : null}
      {editing ? (
        <div className={embedded ? 'rounded-2xl border border-app-border bg-app-surface p-5' : undefined}>
          <JobEditor job={editing === 'new' ? null : editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />
        </div>
      ) : (
        <>
          {list}
          {result}
        </>
      )}
    </>
  );
  if (embedded) return body;
  return (
    <SettingsCard
      title="Geplante Jobs"
      description="Tägliche oder wöchentliche Aufträge. Der Lauf liest nur und handelt als die Person, die den Job anlegt."
      actions={editing ? undefined : <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={() => setEditing('new')}>Neu</button>}
    >
      {body}
    </SettingsCard>
  );
}

function JobEditor({ job, onCancel, onSaved }: { job: Job | null; onCancel: () => void; onSaved: () => void }) {
  const [name, setName] = React.useState(job?.name ?? '');
  const [prompt, setPrompt] = React.useState(job?.prompt ?? '');
  const [cadence, setCadence] = React.useState<'daily' | 'weekly'>(job?.cadence ?? 'weekly');
  const [timeLocal, setTimeLocal] = React.useState(job?.time_local ?? '09:00');
  const [weekday, setWeekday] = React.useState(String(job?.weekday ?? 1));
  const [enabled, setEnabled] = React.useState(job?.enabled ?? true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      name,
      prompt,
      cadence,
      timeLocal,
      weekday: cadence === 'weekly' ? Number(weekday) : null,
      enabled,
    };
    try {
      const res = await fetch(job ? `/api/ai/scheduled-jobs/${encodeURIComponent(job.id)}` : '/api/ai/scheduled-jobs', {
        method: job ? 'PATCH' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await readError(res, 'Job konnte nicht gespeichert werden.'));
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Job konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!job) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/ai/scheduled-jobs/${encodeURIComponent(job.id)}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(await readError(res, 'Job konnte nicht gelöscht werden.'));
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Job konnte nicht gelöscht werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <label className="block">
        <span className={SETTINGS_FIELD_LABEL}>Name</span>
        <input className={`${SETTINGS_INPUT_CLASS} mt-1.5 w-full`} value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <label className="block">
        <span className={SETTINGS_FIELD_LABEL}>Auftrag</span>
        <textarea className={`${SETTINGS_TEXTAREA_CLASS} mt-1.5 w-full`} rows={5} value={prompt} onChange={(event) => setPrompt(event.target.value)} />
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <span className={SETTINGS_FIELD_LABEL}>Rhythmus</span>
          <AppSelectDropdown
            value={cadence}
            onChange={(value) => setCadence(value === 'daily' ? 'daily' : 'weekly')}
            options={[{ value: 'daily', label: 'Täglich' }, { value: 'weekly', label: 'Wöchentlich' }]}
            triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
            className="mt-1.5"
          />
        </div>
        <label className="block">
          <span className={SETTINGS_FIELD_LABEL}>Uhrzeit</span>
          <input className={`${SETTINGS_INPUT_CLASS} mt-1.5 w-full`} value={timeLocal} onChange={(event) => setTimeLocal(event.target.value)} aria-label="Uhrzeit" />
        </label>
      </div>
      {cadence === 'weekly' ? (
        <div>
          <span className={SETTINGS_FIELD_LABEL}>Wochentag</span>
          <AppSelectDropdown value={weekday} onChange={setWeekday} options={WEEKDAYS} triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS} className="mt-1.5" />
        </div>
      ) : null}
      <label className="flex items-center gap-2 text-sm text-app-text">
        <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
        Aktiv
      </label>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      <div className="flex justify-end gap-2">
        {job ? <button type="button" className={`${SETTINGS_BTN_DANGER_OUTLINE} mr-auto`} onClick={() => void remove()}>Löschen</button> : null}
        <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={onCancel}>Abbrechen</button>
        <button type="button" className={SETTINGS_BTN_PRIMARY} disabled={saving} onClick={() => void save()}>Speichern</button>
      </div>
    </div>
  );
}
